/**
 * On-time status vocabulary, mirroring backend purchasing/services/on_time_status.py.
 *
 * CR-6518: Overridden (Early) / Overridden (Late) are native statuses rather
 * than a flag beside the verdict. Each names the status it replaced, so an
 * accepted exception stays distinguishable from a delivery that met its date
 * on its own merits — while still scoring as a pass.
 */

export const ON_TIME = 'on_time';
export const EARLY = 'early';
export const LATE = 'late';
export const OVERRIDDEN_EARLY = 'overridden_early';
export const OVERRIDDEN_LATE = 'overridden_late';

/**
 * What each status is CALLED on screen.
 *
 * CR-7418 (Aaron, 19 Sep 2026): the PO-line delivery donut was printing
 * `overridden_late: 1 (100%)` — *"looks like the raw variable"* — because its
 * own private label map predated CR-6518's native overridden verdicts and it
 * fell through to the key. The words live here now, beside the constants they
 * name, so a surface that learns a new status cannot keep an old vocabulary.
 */
export const ON_TIME_STATUS_LABELS: Record<string, string> = {
  [ON_TIME]: 'On Time',
  [EARLY]: 'Early',
  [LATE]: 'Late',
  [OVERRIDDEN_EARLY]: 'Overridden (Early)',
  [OVERRIDDEN_LATE]: 'Overridden (Late)',
};

/** Statuses that represent an accepted exception rather than a live failure. */
export const OVERRIDDEN_STATUSES: readonly string[] = [OVERRIDDEN_EARLY, OVERRIDDEN_LATE];

/** Statuses that count as a delivery-performance pass. */
export const PASSING_STATUSES: readonly string[] = [ON_TIME, OVERRIDDEN_EARLY, OVERRIDDEN_LATE];

/**
 * Whether a delivery's verdict was overridden and accepted.
 *
 * @param status - the GRN's on_time_status
 * @returns true for the native overridden verdicts
 */
export function isOverridden(status: string | null | undefined): boolean {
  return !!status && OVERRIDDEN_STATUSES.includes(status);
}

/**
 * Whether a delivery counts as on time for scoring and for hiding the
 * exception actions (Acknowledge / Appeal).
 *
 * Use this rather than comparing to `'on_time'`: an overridden delivery has
 * already been reviewed and accepted, so offering to appeal or acknowledge it
 * would invite users to re-litigate a closed decision.
 *
 * @param status - the GRN's on_time_status
 * @returns true when the delivery should be treated as a pass
 */
export function countsAsOnTime(status: string | null | undefined): boolean {
  return !!status && PASSING_STATUSES.includes(status);
}
